import { useEffect } from 'react';
import { usePageSEO } from '@/hooks/usePageSEO';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import HeroArticle from './components/HeroArticle';
import TableMatieres from './components/TableMatieres';
import SectionIntro from './components/SectionIntro';
import PourquoiCritique from './components/PourquoiCritique';
import Les10Criteres from './components/Les10Criteres';
import QuestionsAPoser from './components/QuestionsAPoser';
import SignauxAlarme from './components/SignauxAlarme';
import SoumissionContenu from './components/SoumissionContenu';
import VerificationsOfficielles from './components/VerificationsOfficielles';
import FAQChoisir from './components/FAQChoisir';
import CTAFinal from './components/CTAFinal';
import ArticlesLies from './components/ArticlesLies';

function setMeta(name: string, content: string) {
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function removeOldLdJson() {
  const existing = document.querySelectorAll('script[type="application/ld+json"]');
  existing.forEach((el) => {
    if (el.id === 'blogchoisir-ld') el.remove();
  });
}

export default function BlogChoisirPage() {
  usePageSEO({
    title: "Comment choisir un couvreur à Lévis | 10 critères essentiels",
    description: "Comment éviter les pièges et choisir le bon couvreur à Lévis ? Les 10 critères essentiels, questions à poser, signaux d'alarme. Guide pratique.",
    urlPath: "/blog/comment-choisir-couvreur-levis",
    keywords: "choisir couvreur Lévis, meilleur couvreur Lévis, couvreur fiable, CCQ, soumission",
    ogImage: '/og/og-blog-comment-choisir-couvreur-levis.jpg',
    ogImageAlt: 'Comment choisir un couvreur à Lévis - 10 critères essentiels Guide 2026',
    jsonLdSchema: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://couvreur-levis.com/' },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://couvreur-levis.com/blog' },
        { '@type': 'ListItem', position: 3, name: 'Choisir un couvreur', item: 'https://couvreur-levis.com/blog/comment-choisir-couvreur-levis' },
      ],
    },
  });

  useEffect(() => {
    document.title = "Comment choisir un couvreur à Lévis | 10 critères essentiels";
    setMeta('description', "Comment éviter les pièges et choisir le bon couvreur à Lévis ? Les 10 critères essentiels, questions à poser, signaux d'alarme. Guide pratique.");
    setMeta('keywords', 'choisir couvreur Lévis, meilleur couvreur Lévis, couvreur fiable, CCQ, soumission');

    removeOldLdJson();

    const ld = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          "@id": "https://couvreur-levis.com/blog/comment-choisir-couvreur-levis/#article",
          headline: "Comment choisir un couvreur à Lévis : 10 critères essentiels",
          description: "Guide complet avec 10 critères objectifs pour choisir un couvreur fiable à Lévis.",
          image: "https://readdy.ai/api/search-image?query=Magnifying%20glass%20hovering%20over%20professional%20checklist%20document%20with%20roofing%20contractor%20hard%20hat%20and%20tools%20arranged%20on%20clean%20desk%2C%20bright%20focused%20lighting%20highlighting%20the%20inspection%20details%2C%20orange%20highlighter%20marks%20on%20paper%2C%20modern%20office%20environment%20with%20organized%20paperwork%2C%20deep%20charcoal%20desk%20surface%20with%20burnt%20orange%20accent%20stationery%2C%20photorealistic%20professional%20product%20photography%20with%20quality%20control%20theme&width=1200&height=630&seq=og-blogchoisir&orientation=landscape",
          author: {
            "@type": "Organization",
            name: "Couvreur-Lévis.com",
            url: "https://couvreur-levis.com",
          },
          publisher: {
            "@type": "Organization",
            name: "Couvreur-Lévis.com",
            logo: {
              "@type": "ImageObject",
              url: "https://couvreur-levis.com/og-default.jpg",
            },
          },
          datePublished: "2026-01-20",
          dateModified: "2026-05-16",
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": "https://couvreur-levis.com/blog/comment-choisir-couvreur-levis",
          },
        },
        {
          "@type": "HowTo",
          "@id": "https://couvreur-levis.com/blog/comment-choisir-couvreur-levis/#howto",
          name: "Comment choisir un couvreur à Lévis",
          description: "Méthode en 10 étapes pour choisir un couvreur fiable et certifié.",
          totalTime: "PT2H",
          step: [
            { "@type": "HowToStep", name: "Vérifier la licence", text: "Vérifiez la licence active sur la Régie du bâtiment du Québec." },
            { "@type": "HowToStep", name: "Confirmer certification CCQ", text: "Demandez que les couvreurs soient certifiés CCQ." },
            { "@type": "HowToStep", name: "Vérifier l'assurance RC", text: "Demandez un certificat d'assurance responsabilité civile 2M$." },
            { "@type": "HowToStep", name: "Vérifier CNESST", text: "Demandez une preuve d'inscription à la CNESST." },
            { "@type": "HowToStep", name: "Lire les avis Google", text: "Consultez les avis récents sur Google et la réponse aux critiques." },
            { "@type": "HowToStep", name: "Exiger garantie écrite", text: "Demandez une garantie écrite signée de 10 ans minimum." },
            { "@type": "HowToStep", name: "Analyser la soumission", text: "La soumission doit détailler matériaux, main-d'œuvre, échafaudage." },
            { "@type": "HowToStep", name: "Vérifier salariés vs sous-traitance", text: "Demandez que les couvreurs soient employés salariés." },
            { "@type": "HowToStep", name: "Confirmer adresse physique", text: "Vérifiez que l'entreprise a une adresse réelle sur Google Maps." },
            { "@type": "HowToStep", name: "Vérifier certifications bonus", text: "Maître Couvreur, AMCQ, certifications fabricants." },
          ],
        },
        {
          "@type": "FAQPage",
          "@id": "https://couvreur-levis.com/blog/comment-choisir-couvreur-levis/#faq",
          mainEntity: [
            {
              "@type": "Question",
              name: "Combien de soumissions devrais-je demander ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Trois soumissions détaillées est le sweet spot. Une seule, vous n'avez pas de point de comparaison. Comparez ligne par ligne, pas seulement le prix total.",
              },
            },
            {
              "@type": "Question",
              name: "Est-ce mieux une grosse entreprise ou un petit couvreur local ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Ça dépend. Une grosse entreprise a des processus structurés mais peut sous-traiter. Un petit couvreur local connaît les spécificités de Lévis.",
              },
            },
            {
              "@type": "Question",
              name: "Que faire si je suis insatisfait du travail ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Contactez le couvreur par écrit. Si refus, contactez l'OPC et votre assurance habitation.",
              },
            },
            {
              "@type": "Question",
              name: "Une garantie orale a-t-elle valeur légale ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Techniquement oui, mais pratiquement impossible à prouver. Exigez une garantie écrite signée.",
              },
            },
            {
              "@type": "Question",
              name: "Les couvreurs d'ailleurs sont-ils fiables pour un chantier à Lévis ?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Oui s'ils répondent aux critères de ce guide. Un couvreur local a l'avantage de connaître les codes du quartier et d'intervenir rapidement.",
              },
            },
          ],
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'blogchoisir-ld';
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);

    window.scrollTo(0, 0);

    return () => {
      removeOldLdJson();
    };
  }, []);

  return (
    <div className="font-inter min-h-screen bg-white">
      <Header />
      <main>
        <HeroArticle />

        <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10 pb-16 md:pb-24">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            <TableMatieres />

            <div className="flex-1 min-w-0 space-y-14 md:space-y-20">
              <SectionIntro />
              <PourquoiCritique />
              <Les10Criteres />
              <QuestionsAPoser />
              <SignauxAlarme />
              <SoumissionContenu />
              <VerificationsOfficielles />
              <FAQChoisir />
            </div>
          </div>
        </div>

        <CTAFinal />
        <ArticlesLies />
      </main>
      <Footer />
    </div>
  );
}